// The company's list of tickets, licences and courses (round 27).
//
// Manager-level, not admin: whoever runs the crew is who knows that the new
// bloke's ticket is called an EWP over 11m, and putting the list behind an
// admin-only door would put it out of reach of the people who use it.
//
// Two tabs rather than one list with the archived ones greyed at the bottom.
// An archived code is not a lesser version of an active one — it is a code
// that must not be offered on new records but must still name the old ones —
// and a list mixing the two invites picking the wrong one.
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import {
  competencyCodes,
  validityLabel,
  KIND_LABEL,
  type CompetencyCode,
  type CompetencyKind,
} from '../peopleApi';
import { Banner, Field } from '../ui';
import { ActionMenu, Modal, PageHeader, useConfirm } from '../components/ui';
import { useTabParam } from '../lib/useTabParam';

const TABS = ['active', 'archived'] as const;

export function CompetencyCodesPage() {
  const confirm = useConfirm();
  const [codes, setCodes] = useState<CompetencyCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<CompetencyCode | 'new' | null>(null);
  const [tab, setTab] = useTabParam(TABS, 'active');

  const load = useCallback(async () => {
    try {
      // Both lists in one request; the tabs are a filter over what is already
      // here rather than two round trips.
      const result = await competencyCodes.list({ includeArchived: true });
      setCodes(result.codes);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the codes.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const all = codes ?? [];
  const active = all.filter((c) => !c.archived);
  const archived = all.filter((c) => c.archived);
  const showing = tab === 'archived' ? archived : active;

  async function setArchived(code: CompetencyCode, archive: boolean) {
    try {
      await competencyCodes.archive(code.id, archive);
      setNotice(archive ? `${code.code} archived.` : `${code.code} restored.`);
      setError(null);
      // Follow the row to the tab it just moved to, so it does not simply
      // vanish from under the cursor.
      setTab(archive ? 'archived' : 'active');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not do that.');
    }
  }

  async function remove(code: CompetencyCode) {
    const ok = await confirm({
      title: `Delete ${code.code}?`,
      // Said upfront, because the refusal is the likely outcome and finding
      // out by being refused reads as a broken button.
      message:
        'If anybody holds a record against it the delete is refused, and you can archive it ' +
        'instead; archiving keeps it as the label on the old records and stops it being ' +
        'offered on new ones.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await competencyCodes.remove(code.id);
      setNotice(`${code.code} deleted.`);
      setError(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that.');
      setNotice(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Competency codes"
        subtitle="The licences, tickets and courses your people hold. Used on their records."
        actions={
          <button type="button" className="btn-primary" onClick={() => setEditing('new')}>
            Add code
          </button>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
            className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
              tab === key
                ? 'border-brand-700 font-medium text-brand-800'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {key === 'active' ? 'Active' : 'Archived'}
            <span className="ml-1 text-xs text-gray-500">
              {key === 'active' ? active.length : archived.length}
            </span>
          </button>
        ))}
      </div>

      <section className="panel">
        {codes === null ? (
          <p className="muted">Loading…</p>
        ) : showing.length === 0 ? (
          <p className="muted">
            {tab === 'archived'
              ? 'Nothing archived. Archiving a code keeps it on the records that hold it and stops it being offered on new ones.'
              : 'No codes yet. Add the licences and tickets your crew hold; a competency on somebody’s record takes its name from one of these.'}
          </p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {showing.map((code) => (
              <li
                key={code.id}
                className="flex items-start justify-between gap-3 border-t border-gray-200 pt-2 first:border-0 first:pt-0"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{code.code}</span>
                    <span>{code.name}</span>
                    <span className="pill">{KIND_LABEL[code.kind]}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {validityLabel(code.validityMonths)}
                    {code.description ? ` · ${code.description}` : ''}
                  </div>
                </div>
                <ActionMenu
                  items={[
                    { label: 'Edit', onClick: () => setEditing(code) },
                    code.archived
                      ? { label: 'Restore', onClick: () => void setArchived(code, false) }
                      : { label: 'Archive', onClick: () => void setArchived(code, true) },
                    { label: 'Delete', tone: 'danger', onClick: () => void remove(code) },
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <CodeModal
          code={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setNotice(message);
            setError(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function CodeModal({
  code,
  onClose,
  onSaved,
}: {
  code: CompetencyCode | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [value, setValue] = useState(code?.code ?? '');
  const [name, setName] = useState(code?.name ?? '');
  const [kind, setKind] = useState<CompetencyKind | ''>(code?.kind ?? '');
  const [validity, setValidity] = useState(
    code?.validityMonths === null || code?.validityMonths === undefined
      ? ''
      : String(code.validityMonths)
  );
  const [description, setDescription] = useState(code?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    const body = {
      code: value.trim(),
      name: name.trim(),
      kind: kind as CompetencyKind,
      description: description.trim() || null,
      validityMonths: validity === '' ? null : Number(validity),
    };

    try {
      if (code) {
        await competencyCodes.update(code.id, body);
        onSaved('Saved.');
      } else {
        await competencyCodes.create(body);
        onSaved(`${body.code} added.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={code ? `Edit ${code.code}` : 'Add competency code'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="code-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="code-form" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}

        <Field
          label="Code"
          name="code"
          value={value}
          onChange={setValue}
          error={fields.code}
          hint="The short form people say: WAH, HR, WC."
          autoCapitalize="characters"
          spellCheck={false}
        />
        <Field label="Name" name="name" value={name} onChange={setName} error={fields.name} />

        <label htmlFor="code-kind" className="block">
          Kind
          <select
            id="code-kind"
            className="form-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as CompetencyKind)}
          >
            {/* No default. "Training, because nobody said otherwise" is exactly
                the drift this field exists to prevent. */}
            <option value="">Select…</option>
            <option value="licence">Licence: issued by a regulator</option>
            <option value="ticket">Ticket: issued by an assessor</option>
            <option value="training">Training: a course someone ran</option>
          </select>
          {fields.kind && <span className="field-error">{fields.kind}</span>}
        </label>

        <Field
          label="Valid for (months)"
          name="validityMonths"
          type="number"
          value={validity}
          onChange={setValidity}
          error={fields.validityMonths}
          required={false}
          hint="Leave blank if it doesn’t expire. This only pre-fills the expiry date on a record; you can always change it there."
        />

        <label htmlFor="code-description" className="block">
          Description
          <textarea
            id="code-description"
            className="form-input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </form>
    </Modal>
  );
}
